import React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cn } from "~/lib/cn";
import { Circle } from "lucide-react";

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn("grid gap-2", className)}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

interface RadioGroupItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  children?: React.ReactNode;
  label?: string;
  description?: string;
}

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, children, label, description, id, ...props }, ref) => {
  // Generate a unique ID if not provided
  const itemId = id || React.useId();

  const radioButton = (
    <RadioGroupPrimitive.Item
      ref={ref}
      id={itemId}
      data-slot="radio"
      className={cn(
        "size-4 shrink-0 rounded-full border transition-colors duration-200",
        "flex items-center justify-center",
        "data-[state=checked]:border-brand-700 data-[state=checked]:bg-brand-50 data-[state=checked]:text-brand-700",
        "data-[state=unchecked]:border-gray-200 data-[state=unchecked]:bg-transparent",
        "hover:data-[state=checked]:border-brand-500 hover:data-[state=checked]:bg-transparent hover:data-[state=checked]:text-brand-500",
        "hover:data-[state=unchecked]:border-brand-700 hover:data-[state=unchecked]:bg-brand-50",
        "focus:outline-none focus:ring focus:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-70",
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-indicator"
        className="flex items-center justify-center text-current"
      >
        <Circle className="h-2 w-2 fill-current" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );

  // If no children, label, or description, return just the radio button
  if (!children && !label && !description) {
    return radioButton;
  }

  // If children or label/description provided, wrap with proper layout
  return (
    <div className="flex items-start gap-2 cursor-pointer">
      <div className="flex flex-col justify-center h-5">{radioButton}</div>
      <label
        htmlFor={itemId}
        className={cn(
          "grid gap-1.5 cursor-pointer select-none",
          "peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
        )}
      >
        {label && <span className="text-sm font-normal">{label}</span>}
        {description && (
          <span className="text-sm text-gray-500">{description}</span>
        )}
        {children && <span className="text-sm font-normal">{children}</span>}
      </label>
    </div>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

// Legacy component interface for backward compatibility
const Root = RadioGroup;

interface ItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  label: string;
  description?: string;
}

const Item = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  ItemProps
>(({ label, description, ...props }, ref) => {
  return (
    <RadioGroupItem
      ref={ref}
      label={label}
      description={description}
      {...props}
    />
  );
});
Item.displayName = "RadioGroupItem";

export { RadioGroup, RadioGroupItem };

// Export legacy interface for existing code
export const LegacyRadioGroup = {
  Root,
  Item,
};
